import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Canvas, useFrame, useThree, invalidate } from '@react-three/fiber';
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  ContactShadows,
} from '@react-three/drei';
import { SketchPicker } from 'react-color';
import * as THREE from 'three';
import './KitDesigner3D.css';

/** Use `shirt.glb` per spec, or rename `football_jersey_style_design.glb` → `shirt.glb`. */
export const MODEL_PATH = '/models/shirt.glb';
const MODEL_URL = `${process.env.PUBLIC_URL || ''}${MODEL_PATH}`;

/**
 * Chest-out in **mesh object space** (Object_2). For this glTF the wearer faces **-Z**; chest
 * normals are ~(0,0,-1), back ~(0,0,+1). Mask uses dot(n, chestOut): must be **+1 on chest**,
 * **-1 on back**. Using `(0,0,+1)` inverts that (back gets kd=+1 → mask kills the back; fallback
 * (0,0,1)·(0,0,1)=1 → no text anywhere). If your next model faces +Z, flip to `(0, 0, 1)`.
 */
const KIT_SHIRT_CHEST_OUT_OBJECT = new THREE.Vector3(0, 0, -1).normalize();

/** Use {@link KIT_SHIRT_CHEST_OUT_OBJECT} instead of normal-cluster heuristics. */
const KIT_USE_EXPLICIT_SHIRT_CHEST_AXIS = true;

/**
 * Legacy heuristic only: shirt.glb bind pose notes; nz ≥ τ “front” cluster can be the back sheet.
 */
const KIT_SHIRT_NEGATE_POS_Z_CHEST_DIR = true;

/** Bump when chest-dir logic changes so geometry.userData cache is recomputed (HMR / flag edits). */
const KIT_CHEST_DIR_CACHE_VER = 6;

/** Bump when kit emissive GLSL in applyKitTextOverlay changes (invalidates WebGL program cache on HMR). */
const KIT_EMISSIVE_SHADER_CACHE_VER = 'v28';

/**
 * Negate the chest-out uniform before the shader dot mask. Must stay false with
 * hideChest = smoothstep(..., -nd): true inverts chest vs back and removes all back emissive.
 * If duplicate text appears on the chest only, try true and change the shader to use +nd inside smoothstep instead of -nd.
 */
const KIT_EMISSIVE_FLIP_CHEST_OUT_FOR_SHADER = false;

/**
 * When true, emissive is only multiplied where UV lies in the “back” rect — can hide all text
 * if bounds don’t match the mesh. Default off so lettering shows on the outer back.
 */
const KIT_USE_UV_BACK_RECT_MASK = false;

/**
 * Object Z half-space (see shader). Often zeros the real back; keep off unless you need to hide
 * inner-shell or front bleed after UV/normal masks are on.
 */
const KIT_USE_OBJ_Z_BACK_MASK = false;

/** Object Z: outer back toward +local Z (1) or toward −local Z (−1). Only used if OBJ_Z mask on. */
const KIT_OBJ_Z_BACK_SIGN = -1.0;

/**
 * Normal vs chest-out dot — hides chest when correct; when wrong it kills the back. Default off.
 */
const KIT_USE_NORMAL_EMISSIVE_MASK = false;

/** 512 keeps canvas fills sharp enough and cuts upload cost vs 1024. */
const TEX_SIZE = 512;

/** Rebuilding GPU textures on every keystroke can exhaust WebGL; batch updates. */
const KIT_OVERLAY_TEXTURE_DEBOUNCE_MS = 160;

/** Cap pixel ratio to reduce context-loss risk on integrated GPUs. */
const KIT_MAX_DPR = Math.min(
  typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
  1.5
);

const _kitNn = new THREE.Vector3();
const _kitAccFront = new THREE.Vector3();
const _kitAccBack = new THREE.Vector3();
const _kitWorldFront = new THREE.Vector3();

function shirtFrontWorldFromMesh(mesh) {
  if (!mesh) {
    return _kitWorldFront.copy(KIT_SHIRT_CHEST_OUT_OBJECT).normalize();
  }
  mesh.updateWorldMatrix(true, false);
  const objDir = computeShirtFrontObjectDirection(mesh.geometry);
  _kitWorldFront.copy(objDir).transformDirection(mesh.matrixWorld);
  if (_kitWorldFront.lengthSq() < 1e-12) {
    _kitWorldFront.copy(KIT_SHIRT_CHEST_OUT_OBJECT).transformDirection(mesh.matrixWorld).normalize();
  } else {
    _kitWorldFront.normalize();
  }
  return _kitWorldFront;
}

function commitChestObjectDir(geometry, dir, fromPositiveZHeuristic) {
  const o = dir.clone();
  if (o.lengthSq() > 1e-18) o.normalize();
  if (KIT_SHIRT_NEGATE_POS_Z_CHEST_DIR && fromPositiveZHeuristic) o.negate();
  geometry.userData._kitShirtFrontObjDir = o;
  geometry.userData._kitChestDirCacheVer = KIT_CHEST_DIR_CACHE_VER;
  return o;
}

/**
 * Object-space unit vector pointing out of the chest (for shader dot + back UV classification).
 * Cached on geometry.userData.
 */
function computeShirtFrontObjectDirection(geometry) {
  if (!geometry) {
    return KIT_SHIRT_CHEST_OUT_OBJECT.clone();
  }
  const cached = geometry.userData._kitShirtFrontObjDir;
  if (
    cached &&
    cached.isVector3 &&
    geometry.userData._kitChestDirCacheVer === KIT_CHEST_DIR_CACHE_VER
  ) {
    return cached;
  }

  if (KIT_USE_EXPLICIT_SHIRT_CHEST_AXIS) {
    const o = KIT_SHIRT_CHEST_OUT_OBJECT.clone();
    geometry.userData._kitShirtFrontObjDir = o;
    geometry.userData._kitChestDirCacheVer = KIT_CHEST_DIR_CACHE_VER;
    return o;
  }

  const n = geometry.attributes?.normal;
  if (!n) {
    return commitChestObjectDir(geometry, new THREE.Vector3(0, 0, 1), true);
  }

  const MIN_CLUSTER = 32;
  const thresholdsFront = [0.45, 0.35, 0.28, 0.2, 0.15];
  const thresholdBack = -0.18;

  for (let ti = 0; ti < thresholdsFront.length; ti += 1) {
    const tz = thresholdsFront[ti];
    _kitAccFront.set(0, 0, 0);
    _kitAccBack.set(0, 0, 0);
    let cf = 0;
    let cb = 0;
    for (let i = 0; i < n.count; i += 1) {
      _kitNn.fromBufferAttribute(n, i).normalize();
      if (_kitNn.z >= tz) {
        _kitAccFront.add(_kitNn);
        cf += 1;
      } else if (_kitNn.z <= thresholdBack) {
        _kitAccBack.add(_kitNn);
        cb += 1;
      }
    }
    if (cf >= MIN_CLUSTER && cb >= MIN_CLUSTER) {
      const front = _kitAccFront.clone().divideScalar(cf).normalize();
      const back = _kitAccBack.clone().divideScalar(cb).normalize();
      if (front.dot(back) < -0.25) {
        return commitChestObjectDir(geometry, front, true);
      }
    }
    if (cf >= MIN_CLUSTER) {
      const front = _kitAccFront.clone().divideScalar(cf).normalize();
      return commitChestObjectDir(geometry, front, true);
    }
  }

  _kitAccBack.set(0, 0, 0);
  let cb2 = 0;
  for (let i = 0; i < n.count; i += 1) {
    _kitNn.fromBufferAttribute(n, i).normalize();
    if (_kitNn.z <= thresholdBack) {
      _kitAccBack.add(_kitNn);
      cb2 += 1;
    }
  }
  if (cb2 >= MIN_CLUSTER) {
    /* nz ≤ thresholdBack is the -Z hemisphere on shirt.glb — that is the chest shell, not the back.
       Averaging those normals already gives chest-out; do NOT negate (that flipped to +Z = back-out
       and put emissive on the chest). */
    const chest = _kitAccBack.clone().divideScalar(cb2).normalize();
    return commitChestObjectDir(geometry, chest, false);
  }

  return commitChestObjectDir(geometry, new THREE.Vector3(0, 0, 1), true);
}

const DEFAULT_COLORS = {
  body: '#1e3a5f',
  collar: '#ffffff',
  sleeveLeft: '#2b4c7e',
  sleeveRight: '#2b4c7e',
};

const STYLE_PRESETS = [
  {
    id: 'classic',
    name: 'Classic White',
    colors: { body: '#ffffff', collar: '#000000', sleeveLeft: '#ffffff', sleeveRight: '#ffffff' },
    preview: ['#ffffff', '#000000', '#ffffff'],
  },
  {
    id: 'black',
    name: 'All Black',
    colors: { body: '#111111', collar: '#111111', sleeveLeft: '#111111', sleeveRight: '#111111' },
    preview: ['#111111', '#111111', '#111111'],
  },
  {
    id: 'royal',
    name: 'Royal Blue',
    colors: { body: '#003087', collar: '#ffffff', sleeveLeft: '#003087', sleeveRight: '#003087' },
    preview: ['#003087', '#ffffff', '#003087'],
  },
  {
    id: 'fire',
    name: 'Fire Red',
    colors: { body: '#cc0000', collar: '#ffffff', sleeveLeft: '#cc0000', sleeveRight: '#cc0000' },
    preview: ['#cc0000', '#ffffff', '#cc0000'],
  },
  {
    id: 'forest',
    name: 'Forest Green',
    colors: { body: '#1a5c2a', collar: '#ffffff', sleeveLeft: '#1a5c2a', sleeveRight: '#1a5c2a' },
    preview: ['#1a5c2a', '#ffffff', '#1a5c2a'],
  },
  {
    id: 'gold',
    name: 'Gold & Black',
    colors: { body: '#FFD700', collar: '#111111', sleeveLeft: '#FFD700', sleeveRight: '#FFD700' },
    preview: ['#FFD700', '#111111', '#FFD700'],
  },
];

/**
 * `public/models/shirt.glb` — scene.traverse() logs these mesh names (node names):
 * "Object_2", "Object_3", "Object_4", "Object_5", "Object_6"
 * (Sketchfab merge; internal mesh names are Object_0…Object_4.)
 * Torso: Object_2 = outer fabric shell (kit target). Object_3 = inner liner — same UV/emissive
 * there shows mirrored text inside the neck; keep emissive off Object_3 (see traverse).
 */
const SHIRT_GLB = {
  meshFrontBody: 'Object_2',
  meshBackBody: 'Object_3',
  meshCollar: 'Object_4',
  meshSleeveLeft: 'Object_5',
  meshSleeveRight: 'Object_6',
};

const ZONE_BY_EXACT_MESH_NAME = {
  [SHIRT_GLB.meshFrontBody]: 'body',
  [SHIRT_GLB.meshBackBody]: 'body',
  [SHIRT_GLB.meshCollar]: 'collar',
  [SHIRT_GLB.meshSleeveLeft]: 'sleeveLeft',
  [SHIRT_GLB.meshSleeveRight]: 'sleeveRight',
};

function getColorZone(meshName) {
  if (meshName && ZONE_BY_EXACT_MESH_NAME[meshName] != null) {
    return ZONE_BY_EXACT_MESH_NAME[meshName];
  }
  const n = (meshName || '').toLowerCase();
  if (['collar', 'neck', 'trim', 'collar_band', 'rib_neck'].some((kw) => n.includes(kw))) {
    return 'collar';
  }
  if (
    (n.includes('sleeve') && !n.includes('collar')) ||
    (n.includes('arm') && !n.includes('warm'))
  ) {
    if (n.includes('left') || n.includes('sleeve_l') || n.includes('l_sleeve')) return 'sleeveLeft';
    if (n.includes('right') || n.includes('sleeve_r') || n.includes('r_sleeve')) return 'sleeveRight';
  }
  if (['body', 'torso', 'main', 'shirt', 'jersey', 'cloth', 'chest', 'fabric', 'mesh'].some((kw) => n.includes(kw))) {
    return 'body';
  }
  return 'body';
}

function meshHasPrimaryUV(mesh) {
  const g = mesh.geometry;
  const uv = g && g.attributes && g.attributes.uv;
  return !!(uv && uv.count > 0);
}

/** Axis-aligned bounds of TEXCOORD_0 for this mesh (jersey islands sit in a sub-rect of 0–1). */
function getUvBounds(geometry) {
  const uv = geometry?.attributes?.uv;
  if (!uv || uv.count === 0) return null;
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  for (let i = 0; i < uv.count; i += 1) {
    const u = uv.getX(i);
    const v = uv.getY(i);
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
  }
  return { minU, maxU, minV, maxV };
}

/**
 * UV bounds for chest vs back using **object-space** normals vs the cached chest-out direction
 * (same frame as computeShirtFrontObjectDirection). World-space Z cuts misclassified this glTF
 * after scene layout; dot( n, chestObj ) is stable for unwrap-based text placement.
 */
function getUvBoundsForFacing(geometry, _mesh, facing) {
  const uv = geometry?.attributes?.uv;
  const n = geometry?.attributes?.normal;
  if (!uv || !n || uv.count !== n.count) {
    return getUvBounds(geometry);
  }
  const frontObj = computeShirtFrontObjectDirection(geometry);
  const dotFrontMin = 0.38;
  const dotBackMax = -0.22;
  const nn = new THREE.Vector3();
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  let count = 0;
  for (let i = 0; i < uv.count; i += 1) {
    nn.fromBufferAttribute(n, i).normalize();
    const d = nn.dot(frontObj);
    if (facing === 'front' && d < dotFrontMin) continue;
    if (facing === 'back' && d > dotBackMax) continue;
    const u = uv.getX(i);
    const v = uv.getY(i);
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
    count += 1;
  }
  if (count < 64) {
    return getUvBounds(geometry);
  }
  return { minU, maxU, minV, maxV };
}

/**
 * Torso “side” panels: normals between strong back and chest (typical side / underarm unwrap).
 * Used with {@link getUvBoundsForFacing}(…, 'back') for seam-aware horizontal placement.
 */
function getUvBoundsForSide(geometry, _mesh) {
  const uv = geometry?.attributes?.uv;
  const n = geometry?.attributes?.normal;
  if (!uv || !n || uv.count !== n.count) {
    return null;
  }
  const frontObj = computeShirtFrontObjectDirection(geometry);
  const dotFrontMin = 0.38;
  const dotBackMax = -0.22;
  const nn = new THREE.Vector3();
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  let count = 0;
  for (let i = 0; i < uv.count; i += 1) {
    nn.fromBufferAttribute(n, i).normalize();
    const d = nn.dot(frontObj);
    if (d <= dotBackMax || d >= dotFrontMin) continue;
    const u = uv.getX(i);
    const v = uv.getY(i);
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
    count += 1;
  }
  if (count < 64) {
    return null;
  }
  return { minU, maxU, minV, maxV };
}

/**
 * UV bounds for **everything that is not the chest** (normals with dot( n, chestOut ) below the
 * front threshold). Includes back + sides + underarm so horizontal placement can move across
 * side seams; “back-only” bounds are too narrow and clip text at the vertical torso seam.
 */
function getUvBoundsForNonFront(geometry, _mesh) {
  const uv = geometry?.attributes?.uv;
  const n = geometry?.attributes?.normal;
  if (!uv || !n || uv.count !== n.count) {
    return getUvBounds(geometry);
  }
  const frontObj = computeShirtFrontObjectDirection(geometry);
  const dotFrontMin = 0.38;
  const nn = new THREE.Vector3();
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  let count = 0;
  for (let i = 0; i < uv.count; i += 1) {
    nn.fromBufferAttribute(n, i).normalize();
    const d = nn.dot(frontObj);
    if (d >= dotFrontMin) continue;
    const u = uv.getX(i);
    const v = uv.getY(i);
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
    count += 1;
  }
  if (count < 64) {
    return getUvBounds(geometry);
  }
  return { minU, maxU, minV, maxV };
}

/**
 * Shrink the UV box from the bottom (hem/waistband in this unwrap) so glyphs land on
 * upper back, not the wrap-around hem visible from the front.
 */
function tightenUvBoundsExcludeHem(bounds, hemFraction = 0.18) {
  if (!bounds) return bounds;
  const { minU, maxU, minV, maxV } = bounds;
  const span = maxV - minV;
  if (span <= 1e-6) return bounds;
  const minV2 = minV + span * hemFraction;
  if (minV2 >= maxV - 1e-4) return bounds;
  return { minU, maxU, minV: minV2, maxV };
}

/**
 * Map a point inside the UV island to canvas pixels. uFrac = horizontal (0–1), vFracFromBottom
 * = vertical (0 = hem, 1 = shoulder). Keep mirrorU/mirrorV false here — LTR text is drawn
 * naturally; orientation is fixed via `CanvasTexture.flipY` + optional emissive `repeat`/`offset`, not ctx.scale.
 */
function canvasPointInUvIsland(uvBounds, uFrac, vFracFromBottom, options = {}) {
  const { mirrorU = false, mirrorV = false } = options;
  const uF = Math.min(1, Math.max(0, uFrac));
  const vF = Math.min(1, Math.max(0, vFracFromBottom));
  if (!uvBounds) {
    let u = uF;
    let v = vF;
    if (mirrorU) u = 1 - u;
    if (mirrorV) v = 1 - v;
    return { x: u * TEX_SIZE, y: (1 - v) * TEX_SIZE };
  }
  let u = uvBounds.minU + (uvBounds.maxU - uvBounds.minU) * uF;
  let v = uvBounds.minV + (uvBounds.maxV - uvBounds.minV) * vF;
  if (mirrorU) {
    u = uvBounds.minU + uvBounds.maxU - u;
  }
  if (mirrorV) {
    v = uvBounds.minV + uvBounds.maxV - v;
  }
  return { x: u * TEX_SIZE, y: (1 - v) * TEX_SIZE };
}

function uvSpanU(b) {
  if (!b || !Number.isFinite(b.minU) || !Number.isFinite(b.maxU)) return 0;
  return Math.max(0, b.maxU - b.minU);
}

/**
 * Map horizontal slider 0–1 to absolute texture U. When side and back sit in **disjoint** U
 * intervals (common jersey atlases), the slider walks side span then back span (by U order) so
 * the center never lands in empty atlas space between islands — avoiding clipped/overlapping text
 * when moving from flank toward the back. If regions overlap or data is missing, falls back to
 * linear mapping in `wrapBounds`.
 */
function horizontalSliderToAbsoluteU(uFrac, sideB, backB, wrapBounds) {
  const t = Math.min(1, Math.max(0, uFrac));
  const gap = 0.012;
  const sW = uvSpanU(sideB);
  const bW = uvSpanU(backB);
  const sOk = sideB && sW > 1e-5;
  const bOk = backB && bW > 1e-5;

  if (!sOk && !bOk) {
    if (!wrapBounds || uvSpanU(wrapBounds) < 1e-6) return 0.5;
    return wrapBounds.minU + t * (wrapBounds.maxU - wrapBounds.minU);
  }
  if (sOk && !bOk) return sideB.minU + t * sW;
  if (!sOk && bOk) return backB.minU + t * bW;

  /* Two islands: walk first panel in U, then the other — proportional to each span (skips atlas gaps). */
  let sideFirst;
  if (sideB.maxU < backB.minU - gap) sideFirst = true;
  else if (backB.maxU < sideB.minU - gap) sideFirst = false;
  else {
    const medS = (sideB.minU + sideB.maxU) * 0.5;
    const medB = (backB.minU + backB.maxU) * 0.5;
    sideFirst = medS < medB;
  }
  const first = sideFirst ? sideB : backB;
  const second = sideFirst ? backB : sideB;
  const w1 = uvSpanU(first);
  const w2 = uvSpanU(second);
  const W = w1 + w2;
  if (W < 1e-9) {
    if (!wrapBounds || uvSpanU(wrapBounds) < 1e-6) return backB.minU + t * bW;
    return wrapBounds.minU + t * (wrapBounds.maxU - wrapBounds.minU);
  }
  const f = w1 / W;
  if (f < 1e-9) return second.minU + t * w2;
  if (1 - f < 1e-9) return first.minU + t * w1;
  if (t <= f) return first.minU + (t / f) * w1;
  return second.minU + ((t - f) / (1 - f)) * w2;
}

/**
 * Build a 1D map: horizontal slider → texture U by walking **around the torso in object space**
 * (angle in XZ), using actual mesh UV samples. This avoids atlas “gaps” between unrelated UV
 * islands that side/back AABB logic cannot bridge when those ranges overlap in U.
 *
 * @returns {null | { tMin: number, tMax: number, nBins: number, uMean: number[] }}
 */
function computeTorsoHorizontalCurveFromThetaFn(geometry, thetaFn) {
  const pos = geometry?.attributes?.position;
  const uv = geometry?.attributes?.uv;
  const nAttr = geometry?.attributes?.normal;
  if (!pos || !uv || !nAttr) return null;
  const nv = Math.min(pos.count, uv.count, nAttr.count);
  if (nv < 32) return null;
  const frontObj = computeShirtFrontObjectDirection(geometry);
  const dotFrontMin = 0.38;
  const nn = new THREE.Vector3();
  const thetas = [];
  for (let i = 0; i < nv; i += 1) {
    nn.fromBufferAttribute(nAttr, i).normalize();
    if (nn.dot(frontObj) >= dotFrontMin) continue;
    const x = pos.getX(i);
    const z = pos.getZ(i);
    thetas.push(thetaFn(x, z));
  }
  if (thetas.length < 48) return null;
  let tMin = Infinity;
  let tMax = -Infinity;
  for (let k = 0; k < thetas.length; k += 1) {
    tMin = Math.min(tMin, thetas[k]);
    tMax = Math.max(tMax, thetas[k]);
  }
  const span = tMax - tMin;
  if (span < 1e-4) return null;
  const nBins = 128;
  const sumU = new Array(nBins).fill(0);
  const cnt = new Array(nBins).fill(0);
  for (let i = 0; i < nv; i += 1) {
    nn.fromBufferAttribute(nAttr, i).normalize();
    if (nn.dot(frontObj) >= dotFrontMin) continue;
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const theta = thetaFn(x, z);
    const uu = uv.getX(i);
    const b = Math.min(nBins - 1, Math.floor(((theta - tMin) / span) * nBins));
    sumU[b] += uu;
    cnt[b] += 1;
  }
  const uMean = new Array(nBins);
  for (let i = 0; i < nBins; i += 1) {
    uMean[i] = cnt[i] > 0 ? sumU[i] / cnt[i] : NaN;
  }
  let i0 = 0;
  while (i0 < nBins && Number.isNaN(uMean[i0])) i0 += 1;
  if (i0 >= nBins) return null;
  const seed = uMean[i0];
  for (let j = 0; j < i0; j += 1) uMean[j] = seed;
  for (let j = i0; j < nBins; j += 1) {
    if (Number.isNaN(uMean[j])) uMean[j] = uMean[j - 1];
  }
  for (let pass = 0; pass < 2; pass += 1) {
    for (let j = 1; j < nBins - 1; j += 1) {
      uMean[j] = (uMean[j - 1] + uMean[j] + uMean[j + 1]) / 3;
    }
  }
  let uMin = Infinity;
  let uMax = -Infinity;
  for (let j = 0; j < nBins; j += 1) {
    uMin = Math.min(uMin, uMean[j]);
    uMax = Math.max(uMax, uMean[j]);
  }
  if (uMax - uMin < 0.015) return null;

  return { tMin, tMax, nBins, uMean };
}

function computeTorsoHorizontalCurve(geometry) {
  const a = computeTorsoHorizontalCurveFromThetaFn(geometry, (x, z) => Math.atan2(x, z));
  if (a) return a;
  return computeTorsoHorizontalCurveFromThetaFn(geometry, (x, z) => Math.atan2(x, -z));
}

/** @param {number} t - slider 0..1 */
function uFromTorsoHorizontalCurve(t, curve) {
  if (!curve || !curve.uMean || !curve.nBins) return null;
  const { tMin, tMax, nBins, uMean } = curve;
  const span = tMax - tMin;
  if (span < 1e-8) return uMean[0];
  const tt = Math.min(1, Math.max(0, t));
  const f = tt * (nBins - 1);
  const i0 = Math.min(nBins - 1, Math.floor(f));
  const i1 = Math.min(nBins - 1, i0 + 1);
  const a = f - i0;
  const u = uMean[i0] * (1 - a) + uMean[i1] * a;
  return Math.min(1, Math.max(0, u));
}

/**
 * Emissive canvas: align with GLTF baseColor (`flipY` true), 1:1 UV — no U repeat (that skewed
 * decals on seams). Orientation is corrected via geometry mask + back UV island, not repeat.
 */
function configureKitEmissiveCanvasTexture(tex) {
  if (!tex) return;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.flipY = true;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.repeat.set(1, 1);
  tex.offset.set(0, 0);
  tex.needsUpdate = true;
}

/** Undo kit shader hooks: assigning `undefined` shadows Material.prototype and breaks rendering. */
function stripMaterialProgramOverrides(mat) {
  if (!mat) return;
  delete mat.onBeforeCompile;
  delete mat.customProgramCacheKey;
  delete mat.userData._kitShirtFrontDir;
}

/**
 * Do not replace `map` with a transparent canvas: diffuse is color × map.rgb, so
 * cleared canvas pixels are ~0 and the shirt goes black / text vanishes.
 * Keep the glTF baseColor map and draw name/number on `emissiveMap` instead.
 *
 * Torso UVs often mirror chest+back on the same island (football-style unwrap). UV placement
 * alone cannot hide text on the chest — multiply emissive by a back-face factor in the shader.
 */
/** Mid-Z splitting plane + span; shader uses (z - plane) * sign so back can be min-Z or max-Z side. */
function computeKitObjZPlane(geometry) {
  if (!geometry) return { zPlane: 0, zSpan: 1 };
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const bb = geometry.boundingBox;
  const zSpan = Math.max(1e-6, bb.max.z - bb.min.z);
  const zPlane = (bb.min.z + bb.max.z) * 0.5;
  return { zPlane, zSpan };
}

function applyKitTextOverlay(mat, canvasTexture, torsoMesh, backUvBounds) {
  if (!mat || !canvasTexture || !torsoMesh) return;
  configureKitEmissiveCanvasTexture(canvasTexture);
  stripMaterialProgramOverrides(mat);

  const { zPlane, zSpan } = computeKitObjZPlane(torsoMesh.geometry);

  if (!mat.userData._kitShirtFrontDir) {
    mat.userData._kitShirtFrontDir = new THREE.Vector3();
  }
  shirtFrontWorldFromMesh(torsoMesh);
  mat.userData._kitShirtFrontDir.copy(_kitWorldFront);
  if (KIT_EMISSIVE_FLIP_CHEST_OUT_FOR_SHADER) mat.userData._kitShirtFrontDir.negate();

  if (!mat.userData._kitBackUvMin) mat.userData._kitBackUvMin = new THREE.Vector2();
  if (!mat.userData._kitBackUvMax) mat.userData._kitBackUvMax = new THREE.Vector2();

  const tight = backUvBounds ? tightenUvBoundsExcludeHem(backUvBounds, 0.18) : null;
  let maskActive = 0;
  if (
    KIT_USE_UV_BACK_RECT_MASK &&
    tight &&
    Number.isFinite(tight.minU) &&
    Number.isFinite(tight.maxU) &&
    tight.maxU - tight.minU > 1e-5 &&
    tight.maxV - tight.minV > 1e-5
  ) {
    const uSpan = tight.maxU - tight.minU;
    const vSpan = tight.maxV - tight.minV;
    const padU = Math.max(0.004, uSpan * 0.04);
    const padV = Math.max(0.004, vSpan * 0.04);
    mat.userData._kitBackUvMin.set(
      Math.max(0, tight.minU - padU),
      Math.max(0, tight.minV - padV)
    );
    mat.userData._kitBackUvMax.set(
      Math.min(1, tight.maxU + padU),
      Math.min(1, tight.maxV + padV)
    );
    maskActive = 1;
  } else {
    mat.userData._kitBackUvMin.set(0, 0);
    mat.userData._kitBackUvMax.set(1, 1);
    maskActive = 0;
  }
  mat.userData._kitUvMaskActive = maskActive;
  mat.userData._kitNormalMaskActive = KIT_USE_NORMAL_EMISSIVE_MASK ? 1 : 0;
  mat.userData._kitObjZMaskActive = KIT_USE_OBJ_Z_BACK_MASK ? 1 : 0;

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.kitBackUvMin = { value: mat.userData._kitBackUvMin };
    shader.uniforms.kitBackUvMax = { value: mat.userData._kitBackUvMax };
    shader.uniforms.kitUvMaskActive = { value: mat.userData._kitUvMaskActive };
    shader.uniforms.kitNormalMaskActive = { value: mat.userData._kitNormalMaskActive };
    shader.uniforms.kitObjZMaskActive = { value: mat.userData._kitObjZMaskActive };
    shader.uniforms.kitShirtFrontWorld = { value: mat.userData._kitShirtFrontDir };
    shader.uniforms.kitObjZPlane = { value: zPlane };
    shader.uniforms.kitObjZSpan = { value: zSpan };
    shader.uniforms.kitObjZBackSign = { value: KIT_OBJ_Z_BACK_SIGN };

    if (!shader.vertexShader.includes('vKitObjPos')) {
      shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        `#include <common>
varying vec3 vKitObjPos;
`
      );
      shader.vertexShader = shader.vertexShader.replace(
        '#include <project_vertex>',
        `vKitObjPos = transformed;
#include <project_vertex>
`
      );
    }

    const commonInc = '#include <common>';
    const emissiveInc = '#include <emissivemap_fragment>';
    let frag = shader.fragmentShader;
    if (!frag.includes(commonInc) && process.env.NODE_ENV === 'development') {
      console.error('[KitDesigner3D] kit shader: missing', commonInc);
    } else {
      frag = frag.replace(
        commonInc,
        `${commonInc}
varying vec3 vKitObjPos;
uniform vec2 kitBackUvMin;
uniform vec2 kitBackUvMax;
uniform float kitUvMaskActive;
uniform float kitNormalMaskActive;
uniform float kitObjZMaskActive;
uniform vec3 kitShirtFrontWorld;
uniform float kitObjZPlane;
uniform float kitObjZSpan;
uniform float kitObjZBackSign;
`
      );
    }
    if (!frag.includes(emissiveInc) && process.env.NODE_ENV === 'development') {
      console.error('[KitDesigner3D] kit shader: missing', emissiveInc, 'type:', mat.type);
    } else {
      const beforeEmissive = frag;
      frag = frag.replace(
        emissiveInc,
        `${emissiveInc}
#ifdef USE_EMISSIVEMAP
{
	vec2 kuv = vEmissiveMapUv;
	float inUvBack = step( kitBackUvMin.x, kuv.x ) * step( kuv.x, kitBackUvMax.x ) * step( kitBackUvMin.y, kuv.y ) * step( kuv.y, kitBackUvMax.y );
	vec3 nView = normalize( nonPerturbedNormal );
	vec3 chestV = normalize( transformDirection( kitShirtFrontWorld, viewMatrix ) );
	/* Sign fix: glTF root rotation can make raw dot negative on chest; −dot so chest nd>0 → kill emissive */
	float nd = -dot( nView, chestV );
	float hideChest = 1.0 - smoothstep( -0.35, 0.22, nd );
	float chestKill = mix( 1.0, hideChest, kitNormalMaskActive );
	float zBand = max( kitObjZSpan * 0.06, 0.001 );
	float dz = ( vKitObjPos.z - kitObjZPlane ) * kitObjZBackSign;
	float onZ = mix( 1.0, smoothstep( -zBand, zBand, dz ), kitObjZMaskActive );
	totalEmissiveRadiance *= mix( 1.0, inUvBack, kitUvMaskActive ) * chestKill * onZ;
}
#endif
`
      );
      if (frag === beforeEmissive && process.env.NODE_ENV === 'development') {
        console.error('[KitDesigner3D] kit shader: emissive patch did not apply (include string mismatch?)');
      }
    }
    shader.fragmentShader = frag;
  };
  /*
   * Per-material uuid: WebGLRenderer shares programs globally by programCacheKey; another mesh
   * compiling first with the same key can otherwise skip this material's onBeforeCompile output.
   * Bump KIT_EMISSIVE_SHADER_CACHE_VER when changing the injected GLSL so HMR invalidates programs.
   */
  mat.customProgramCacheKey = () =>
    `kit_cricket_emissive_uv_${KIT_EMISSIVE_SHADER_CACHE_VER}_${mat.uuid}`;

  if (mat.userData._kitBaseMap === undefined && mat.map != null) {
    mat.userData._kitBaseMap = mat.map;
  }
  mat.map = mat.userData._kitBaseMap ?? mat.map;
  mat.emissiveMap = canvasTexture;
  /* Same TEXCOORD as baseColor — otherwise emissive samples UV2/black on back while chest hits UV0. */
  if (mat.map && mat.emissiveMap) {
    mat.emissiveMap.channel = mat.map.channel;
  }
  mat.emissive.set(0xffffff);
  mat.emissiveIntensity = 5;
  mat.side = THREE.FrontSide;
  mat.needsUpdate = true;
}

function cloneMeshMaterials(mesh) {
  if (mesh.userData._kitMaterialsCloned) return;
  if (Array.isArray(mesh.material)) {
    mesh.material = mesh.material.map((m) => (m && m.clone ? m.clone() : m));
  } else if (mesh.material && mesh.material.clone) {
    mesh.material = mesh.material.clone();
  }
  mesh.userData._kitMaterialsCloned = true;
}

function applyZoneColors(scene, colors) {
  scene.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    cloneMeshMaterials(child);
    const zone = getColorZone(child.name);
    const hex = colors[zone];
    if (!hex) return;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    mats.forEach((mat) => {
      if (mat && mat.color) {
        mat.color.set(hex);
        mat.needsUpdate = true;
      }
    });
  });
}

function getCanvasFont(fontId, sizePx) {
  switch (fontId) {
    case 'boldBlock':
      return `900 ${sizePx}px "Arial Black", "Arial", sans-serif`;
    case 'italicSports':
      return `italic 800 ${sizePx}px Arial, sans-serif`;
    case 'classicSerif':
      return `${sizePx}px Georgia, "Times New Roman", serif`;
    case 'modernSans':
    default:
      return `${sizePx}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  }
}

/** User-placed lines on the back UV island (fractions 0–1 within the island). */
function createCustomLabelRow() {
  return {
    id: `cl-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    text: '',
    u: 0.5,
    v: 0.55,
    fontId: 'modernSans',
    size: 22,
    color: '#ffffff',
    /** When set by raycast drag, texture UV (0–1); takes precedence over u/v sliders in the canvas. */
    dragU: undefined,
    dragV: undefined,
  };
}

/** Where the logo is printed — each area has its own mesh / UV sheet (up to 4 logos total). */
const LOGO_ZONE_OPTIONS = [
  { id: 'chest', label: 'Chest (front)' },
  { id: 'back', label: 'Back' },
  { id: 'sleeveLeft', label: 'Left sleeve' },
  { id: 'sleeveRight', label: 'Right sleeve' },
];

const MAX_CUSTOM_LOGOS = 4;

function createCustomLogoRow(zone = 'chest') {
  return {
    id: `logo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    zone,
    imageDataUrl: '',
    u: 0.5,
    v: 0.5,
    scale: 0.22,
    dragU: undefined,
    dragV: undefined,
  };
}

function tightenZoneBounds(bounds) {
  if (!bounds) return null;
  return tightenUvBoundsExcludeHem(bounds, 0.18);
}

function getLogoZoneBounds(zone, kit) {
  switch (zone) {
    case 'chest':
      return tightenZoneBounds(kit?.front ?? null);
    case 'back':
      return tightenZoneBounds(kit?.back ?? null);
    case 'sleeveLeft':
      return tightenZoneBounds(kit?.sleeveLeft ?? null);
    case 'sleeveRight':
      return tightenZoneBounds(kit?.sleeveRight ?? null);
    default:
      return tightenZoneBounds(kit?.front ?? null);
  }
}

/**
 * Infer chest / back / sleeve from the raycast hit so a logo can be placed with one click,
 * the same way as text (no need to pre-pick a zone that matches the click).
 */
function inferLogoZoneFromHit(meshName, u, v, kit) {
  if (meshName === SHIRT_GLB.meshSleeveLeft) return 'sleeveLeft';
  if (meshName === SHIRT_GLB.meshSleeveRight) return 'sleeveRight';
  if (meshName === SHIRT_GLB.meshFrontBody) {
    const fb = getLogoZoneBounds('chest', kit);
    const bb = getLogoZoneBounds('back', kit);
    const inRect = (b) =>
      b &&
      u >= b.minU &&
      u <= b.maxU &&
      v >= b.minV &&
      v <= b.maxV;
    const inF = inRect(fb);
    const inB = inRect(bb);
    if (inF && inB) return 'chest';
    if (inF) return 'chest';
    if (inB) return 'back';
    return null;
  }
  return null;
}

/** Logo placement: horizontal / vertical 0–1 within that zone’s UV rect (same idea as text on an island). */
function getCanvasPlacementXYForLogo(logo, uvKit) {
  const zb = getLogoZoneBounds(logo.zone, uvKit);
  const u = Math.min(1, Math.max(0, Number(logo.u) || 0.5));
  const v = Math.min(1, Math.max(0, Number(logo.v) || 0.5));
  if (Number.isFinite(logo.dragU) && Number.isFinite(logo.dragV)) {
    return {
      x: Math.min(1, Math.max(0, logo.dragU)) * TEX_SIZE,
      y: (1 - Math.min(1, Math.max(0, logo.dragV))) * TEX_SIZE,
    };
  }
  if (!zb || zb.maxU - zb.minU < 1e-6 || zb.maxV - zb.minV < 1e-6) {
    return { x: TEX_SIZE * 0.5, y: TEX_SIZE * 0.5 };
  }
  const uAbs = zb.minU + (zb.maxU - zb.minU) * u;
  const vAbs = zb.minV + (zb.maxV - zb.minV) * v;
  return { x: uAbs * TEX_SIZE, y: (1 - vAbs) * TEX_SIZE };
}

function syncSlidersFromTextureUvForLogo(dragU, dragV, zone, kit) {
  const zb = getLogoZoneBounds(zone, kit);
  if (!zb || zb.maxU - zb.minU < 1e-6 || zb.maxV - zb.minV < 1e-6) {
    return { u: 0.5, v: 0.5 };
  }
  const u = (dragU - zb.minU) / (zb.maxU - zb.minU);
  const v = (dragV - zb.minV) / (zb.maxV - zb.minV);
  return {
    u: Math.min(1, Math.max(0, u)),
    v: Math.min(1, Math.max(0, v)),
  };
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = src;
  });
}

/** @returns {{ x: number, y: number }} canvas pixel center for label/logo placement */
function getCanvasPlacementXY(row, uvKit) {
  const wrapRaw = uvKit.wrap ?? null;
  const sideB = uvKit.side ?? null;
  const backB = uvKit.back ?? null;
  const torsoH = uvKit.torsoHorizontal ?? null;
  const uvBack = wrapRaw ? tightenUvBoundsExcludeHem(wrapRaw, 0.18) : null;
  const u = Math.min(1, Math.max(0, Number(row.u) || 0.5));
  const v = Math.min(1, Math.max(0, Number(row.v) || 0.5));
  const dU = row.dragU;
  const dV = row.dragV;
  if (Number.isFinite(dU) && Number.isFinite(dV)) {
    return {
      x: Math.min(1, Math.max(0, dU)) * TEX_SIZE,
      y: (1 - Math.min(1, Math.max(0, dV))) * TEX_SIZE,
    };
  }
  if (uvBack) {
    const uTorso = uFromTorsoHorizontalCurve(u, torsoH);
    const uAbs = Number.isFinite(uTorso)
      ? uTorso
      : horizontalSliderToAbsoluteU(u, sideB, backB, wrapRaw);
    const vAbs = uvBack.minV + (uvBack.maxV - uvBack.minV) * v;
    return { x: uAbs * TEX_SIZE, y: (1 - vAbs) * TEX_SIZE };
  }
  const pt = canvasPointInUvIsland(null, u, v);
  return { x: pt.x, y: pt.y };
}

/** After a drag hit, approximate slider u/v so the range inputs stay in sync. */
function syncSlidersFromTextureUv(dragU, dragV, kit) {
  const wrapRaw = kit?.wrap ?? null;
  const uvBack = wrapRaw ? tightenUvBoundsExcludeHem(wrapRaw, 0.18) : null;
  let v = 0.55;
  if (uvBack && uvBack.maxV - uvBack.minV > 1e-6) {
    v = (dragV - uvBack.minV) / (uvBack.maxV - uvBack.minV);
    v = Math.min(1, Math.max(0, v));
  }
  let u = 0.5;
  const torsoH = kit?.torsoHorizontal;
  if (torsoH && torsoH.uMean && torsoH.nBins) {
    let lo = 0;
    let hi = 1;
    for (let k = 0; k < 28; k += 1) {
      const mid = (lo + hi) * 0.5;
      const val = uFromTorsoHorizontalCurve(mid, torsoH);
      if (val < dragU) lo = mid;
      else hi = mid;
    }
    u = (lo + hi) * 0.5;
  } else if (wrapRaw && uvSpanU(wrapRaw) > 1e-6) {
    u = (dragU - wrapRaw.minU) / (wrapRaw.maxU - wrapRaw.minU);
    u = Math.min(1, Math.max(0, u));
  }
  return { u, v };
}

async function drawLogoOnCtx(ctx, logo, uvKit, getXY) {
  const src = logo.imageDataUrl;
  if (!src || typeof src !== 'string') return;
  let img;
  try {
    img = await loadImageElement(src);
  } catch {
    return;
  }
  const { x, y } = getXY(logo, uvKit);
  const scale = Math.min(0.48, Math.max(0.06, Number(logo.scale) || 0.2));
  const maxPx = scale * TEX_SIZE;
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (iw < 1 || ih < 1) return;
  const aspect = iw / ih;
  let dw;
  let dh;
  if (aspect >= 1) {
    dw = maxPx;
    dh = maxPx / aspect;
  } else {
    dh = maxPx;
    dw = maxPx * aspect;
  }
  ctx.save();
  ctx.drawImage(img, x - dw * 0.5, y - dh * 0.5, dw, dh);
  ctx.restore();
}

/**
 * Torso canvas: custom text + logos on chest & back only.
 * Sleeve canvases: logos for that arm only (separate mesh UV space).
 */
async function buildAllKitOverlayTexturesAsync({ customLabels = [], customLogos = [] }, uvKit = {}) {
  try {
    const makeCanvas = () => {
      const canvas = document.createElement('canvas');
      canvas.width = TEX_SIZE;
      canvas.height = TEX_SIZE;
      return canvas;
    };

    const torsoCanvas = makeCanvas();
    const tctx = torsoCanvas.getContext('2d');
    if (!tctx) return null;
    tctx.clearRect(0, 0, TEX_SIZE, TEX_SIZE);

    for (let i = 0; i < customLabels.length; i += 1) {
      const row = customLabels[i];
      const line = (row.text || '').trim();
      if (!line) continue;
      tctx.save();
      const fontId = row.fontId || 'modernSans';
      const sizePx = Math.min(120, Math.max(8, Number(row.size) || 22));
      tctx.font = getCanvasFont(fontId, sizePx);
      if (fontId === 'boldBlock') tctx.letterSpacing = '2px';
      tctx.fillStyle = row.color || '#ffffff';
      tctx.textAlign = 'center';
      tctx.textBaseline = 'middle';
      const { x, y } = getCanvasPlacementXY(row, uvKit);
      tctx.fillText(line, x, y);
      tctx.restore();
    }

    const torsoLogos = customLogos.filter((l) => l.zone === 'chest' || l.zone === 'back');
    for (let j = 0; j < torsoLogos.length; j += 1) {
      await drawLogoOnCtx(tctx, torsoLogos[j], uvKit, getCanvasPlacementXYForLogo);
    }

    const buildSleeveCanvas = async (zone) => {
      const logos = customLogos.filter((l) => l.zone === zone);
      if (!logos.length) return null;
      const canvas = makeCanvas();
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.clearRect(0, 0, TEX_SIZE, TEX_SIZE);
      for (let k = 0; k < logos.length; k += 1) {
        await drawLogoOnCtx(ctx, logos[k], uvKit, getCanvasPlacementXYForLogo);
      }
      const tex = new THREE.CanvasTexture(canvas);
      configureKitEmissiveCanvasTexture(tex);
      return tex;
    };

    const torsoTex = new THREE.CanvasTexture(torsoCanvas);
    configureKitEmissiveCanvasTexture(torsoTex);

    const sleeveLeftTex = await buildSleeveCanvas('sleeveLeft');
    const sleeveRightTex = await buildSleeveCanvas('sleeveRight');

    return {
      torso: torsoTex,
      sleeveLeft: sleeveLeftTex,
      sleeveRight: sleeveRightTex,
    };
  } catch (e) {
    console.warn('[KitDesigner3D] buildAllKitOverlayTexturesAsync', e);
    return null;
  }
}

function KitModel({ colors, overlayTextures, onKitUvBoundsReady, kitUvBounds }) {
  const { scene } = useGLTF(MODEL_URL);
  const logged = useRef(false);
  const boundsCbRef = useRef(onKitUvBoundsReady);
  boundsCbRef.current = onKitUvBoundsReady;

  useFrame(() => {
    const refTorso = scene.getObjectByName(SHIRT_GLB.meshFrontBody);
    if (!refTorso?.isMesh) return;
    shirtFrontWorldFromMesh(refTorso);
    [SHIRT_GLB.meshFrontBody, SHIRT_GLB.meshSleeveLeft, SHIRT_GLB.meshSleeveRight].forEach(
      (meshName) => {
        const part = scene.getObjectByName(meshName);
        if (!part?.isMesh) return;
        const mats = Array.isArray(part.material) ? part.material : [part.material];
        mats.forEach((m) => {
          if (!m?.userData._kitShirtFrontDir) return;
          m.userData._kitShirtFrontDir.copy(_kitWorldFront);
          if (KIT_EMISSIVE_FLIP_CHEST_OUT_FOR_SHADER) m.userData._kitShirtFrontDir.negate();
        });
      }
    );
  });

  useEffect(() => {
    const torso = scene.getObjectByName(SHIRT_GLB.meshFrontBody);
    if (!torso?.isMesh || !meshHasPrimaryUV(torso)) {
      boundsCbRef.current?.({
        front: null,
        back: null,
        wrap: null,
        side: null,
        torsoHorizontal: null,
        sleeveLeft: null,
        sleeveRight: null,
      });
      return;
    }
    const geo = torso.geometry;
    const front = getUvBoundsForFacing(geo, torso, 'front');
    const back = getUvBoundsForFacing(geo, torso, 'back');
    const wrap = getUvBoundsForNonFront(geo, torso);
    const side = getUvBoundsForSide(geo, torso);
    const torsoHorizontal = computeTorsoHorizontalCurve(geo);
    const sl = scene.getObjectByName(SHIRT_GLB.meshSleeveLeft);
    const sr = scene.getObjectByName(SHIRT_GLB.meshSleeveRight);
    const sleeveLeft = sl?.isMesh && meshHasPrimaryUV(sl) ? getUvBounds(sl.geometry) : null;
    const sleeveRight = sr?.isMesh && meshHasPrimaryUV(sr) ? getUvBounds(sr.geometry) : null;
    boundsCbRef.current?.({
      front,
      back,
      wrap,
      side,
      torsoHorizontal,
      sleeveLeft,
      sleeveRight,
    });
  }, [scene]);

  useEffect(() => {
    if (logged.current) return;
    logged.current = true;
    if (process.env.NODE_ENV !== 'development') return;
    console.log('[KitDesigner3D] ===== scene.traverse() mesh report =====');
    const names = [];
    scene.traverse((o) => {
      if (o.isMesh) {
        names.push(o.name);
        console.log(`mesh: "${o.name}"`, 'type:', o.type);
      }
    });
    console.log(
      '[KitDesigner3D] mesh names (exact, for matching):',
      JSON.stringify(names)
    );
    console.log('[KitDesigner3D] ===== end mesh report =====');
  }, [scene]);

  useEffect(() => {
    applyZoneColors(scene, colors);
  }, [scene, colors]);

  useEffect(() => {
    const torsoMaskBounds = kitUvBounds?.wrap ?? kitUvBounds?.back ?? null;
    const slMask = kitUvBounds?.sleeveLeft ?? null;
    const srMask = kitUvBounds?.sleeveRight ?? null;

    scene.traverse((child) => {
      if (!child.isMesh || !child.material) return;
      const name = child.name;

      if (name === SHIRT_GLB.meshBackBody) {
        cloneMeshMaterials(child);
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach((mat) => {
          if (!mat) return;
          if (mat.userData._kitBaseMap !== undefined) mat.map = mat.userData._kitBaseMap;
          mat.emissiveMap = null;
          mat.emissive.setHex(0x000000);
          mat.emissiveIntensity = 0;
          stripMaterialProgramOverrides(mat);
          mat.needsUpdate = true;
        });
        return;
      }

      const isTorso = name === SHIRT_GLB.meshFrontBody;
      const isSL = name === SHIRT_GLB.meshSleeveLeft;
      const isSR = name === SHIRT_GLB.meshSleeveRight;
      if (!isTorso && !isSL && !isSR) return;
      if (!meshHasPrimaryUV(child)) {
        console.warn('[KitDesigner3D] skip overlay: no UV on mesh', name);
        return;
      }

      let tex = null;
      let maskBounds = torsoMaskBounds;
      if (isTorso) tex = overlayTextures?.torso ?? null;
      else if (isSL) {
        tex = overlayTextures?.sleeveLeft ?? null;
        maskBounds = slMask;
      } else if (isSR) {
        tex = overlayTextures?.sleeveRight ?? null;
        maskBounds = srMask;
      }

      cloneMeshMaterials(child);
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((mat) => {
        if (!mat) return;
        if (tex) {
          applyKitTextOverlay(mat, tex, child, maskBounds);
        } else {
          if (mat.userData._kitBaseMap !== undefined) mat.map = mat.userData._kitBaseMap;
          mat.emissiveMap = null;
          mat.emissive.setHex(0x000000);
          mat.emissiveIntensity = 0;
          stripMaterialProgramOverrides(mat);
          mat.needsUpdate = true;
        }
      });
    });
  }, [scene, overlayTextures, kitUvBounds]);

  return <primitive object={scene} />;
}

function Loader() {
  return (
    <mesh>
      <boxGeometry args={[0.6, 0.8, 0.12]} />
      <meshStandardMaterial color="#1e1e2e" wireframe />
    </mesh>
  );
}

/**
 * Raycast against the outer torso mesh; pointer UVs update the active custom label (drag-to-place).
 */
function KitPlacementDragPlacer({
  enabled,
  activePlacement,
  kitUvBounds,
  onDragUv,
}) {
  const { camera, gl, scene } = useThree();
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const ndc = useMemo(() => new THREE.Vector2(), []);
  /** Removes pointermove/up listeners from an in-progress shirt placement drag. */
  const dragListenersCleanupRef = useRef(null);

  const hitPlacement = useCallback(
    (clientX, clientY) => {
      const rect = gl.domElement.getBoundingClientRect();
      ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const meshes = [
        scene.getObjectByName(SHIRT_GLB.meshFrontBody),
        scene.getObjectByName(SHIRT_GLB.meshSleeveLeft),
        scene.getObjectByName(SHIRT_GLB.meshSleeveRight),
      ].filter(Boolean);
      const hits = raycaster.intersectObjects(meshes, false);
      const h = hits[0];
      if (!h || h.uv == null) return null;
      const u = h.uv.x;
      const v = h.uv.y;
      const meshName = h.object.name;

      if (activePlacement?.type === 'label') {
        if (meshName !== SHIRT_GLB.meshFrontBody) return null;
        return { u, v };
      }

      if (activePlacement?.type === 'logo') {
        const zone = inferLogoZoneFromHit(meshName, u, v, kitUvBounds);
        if (!zone) return null;
        return { u, v, zone };
      }
      return null;
    },
    [camera, gl, scene, raycaster, ndc, activePlacement, kitUvBounds]
  );

  useEffect(() => {
    if (!enabled || !activePlacement) return;
    const { type, id } = activePlacement;
    const el = gl.domElement;

    const onDown = (e) => {
      if (e.button !== 0) return;
      const hit = hitPlacement(e.clientX, e.clientY);
      if (!hit) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      dragListenersCleanupRef.current?.();

      const onMoveDrag = (ev) => {
        ev.preventDefault();
        ev.stopImmediatePropagation();
        const h = hitPlacement(ev.clientX, ev.clientY);
        if (!h) return;
        if (type === 'logo' && h.zone != null) {
          onDragUv(type, id, h.u, h.v, { zone: h.zone });
        } else {
          onDragUv(type, id, h.u, h.v);
        }
      };

      const onUpDrag = () => {
        el.removeEventListener('pointermove', onMoveDrag);
        el.removeEventListener('pointerup', onUpDrag);
        el.removeEventListener('pointercancel', onUpDrag);
        window.removeEventListener('pointerup', onUpDrag);
        window.removeEventListener('pointercancel', onUpDrag);
        dragListenersCleanupRef.current = null;
      };

      el.addEventListener('pointermove', onMoveDrag, { passive: false });
      el.addEventListener('pointerup', onUpDrag);
      el.addEventListener('pointercancel', onUpDrag);
      window.addEventListener('pointerup', onUpDrag);
      window.addEventListener('pointercancel', onUpDrag);
      dragListenersCleanupRef.current = onUpDrag;

      if (type === 'logo' && hit.zone != null) {
        onDragUv(type, id, hit.u, hit.v, { zone: hit.zone });
      } else {
        onDragUv(type, id, hit.u, hit.v);
      }
    };

    const captureOpts = { capture: true, passive: false };
    el.addEventListener('pointerdown', onDown, captureOpts);
    return () => {
      dragListenersCleanupRef.current?.();
      dragListenersCleanupRef.current = null;
      el.removeEventListener('pointerdown', onDown, captureOpts);
    };
  }, [enabled, activePlacement, gl, hitPlacement, onDragUv]);

  useEffect(() => {
    gl.domElement.style.cursor = enabled && activePlacement ? 'crosshair' : '';
    return () => {
      gl.domElement.style.cursor = '';
    };
  }, [enabled, activePlacement, gl]);

  return null;
}

function loadImageFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = dataUrl;
  });
}

/**
 * Single 1024×1024 sheet: Front | Back / Left sleeve | Right sleeve (labeled).
 * Used as the main order preview so fulfillment sees all areas.
 */
async function buildKitOrderCompositeImage(viewImages) {
  if (!viewImages) return '';
  const order = ['front', 'back', 'sleeveLeft', 'sleeveRight'];
  const labels = ['Front', 'Back', 'Left sleeve', 'Right sleeve'];
  const urls = order.map((k) => viewImages[k]).filter(Boolean);
  if (urls.length === 0) return '';
  const imgs = await Promise.all(urls.map((u) => loadImageFromDataUrl(u)));
  const qw = 512;
  const qh = 512;
  const canvas = document.createElement('canvas');
  canvas.width = qw * 2;
  canvas.height = qh * 2;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = '#0a0a0f';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const positions = [
    [0, 0],
    [qw, 0],
    [0, qh],
    [qw, qh],
  ];
  for (let i = 0; i < Math.min(4, imgs.length); i += 1) {
    const [px, py] = positions[i];
    ctx.drawImage(imgs[i], px, py, qw, qh);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(px, py, qw, 28);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 14px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.fillText(labels[i], px + 8, py + 18);
  }
  return canvas.toDataURL('image/png');
}

/**
 * Renders four preset camera angles (front, back, sleeves) for order fulfillment.
 */
function KitMultiViewCapture({ captureViewsRef }) {
  const { gl, scene, camera } = useThree();
  const controls = useThree((s) => s.controls);

  useEffect(() => {
    const lookAt = new THREE.Vector3(0, 0.12, 0);
    const views = [
      ['front', new THREE.Vector3(0, 0.38, 2.55)],
      ['back', new THREE.Vector3(0, 0.38, -2.55)],
      ['sleeveLeft', new THREE.Vector3(2.35, 0.3, 0.32)],
      ['sleeveRight', new THREE.Vector3(-2.35, 0.3, 0.32)],
    ];

    const capture = async () => {
      const origPos = camera.position.clone();
      const origQuat = camera.quaternion.clone();
      const origTarget = controls?.target?.clone?.() ?? new THREE.Vector3(0, 0, 0);

      const out = {};
      for (let i = 0; i < views.length; i += 1) {
        const [key, pos] = views[i];
        camera.position.copy(pos);
        camera.lookAt(lookAt);
        if (controls) {
          controls.target.copy(lookAt);
          controls.update();
        }
        camera.updateProjectionMatrix();
        gl.render(scene, camera);
        await new Promise((r) => requestAnimationFrame(r));
        gl.render(scene, camera);
        out[key] = gl.domElement.toDataURL('image/png');
      }

      camera.position.copy(origPos);
      camera.quaternion.copy(origQuat);
      if (controls) {
        controls.target.copy(origTarget);
        controls.update();
      }
      camera.updateProjectionMatrix();
      gl.render(scene, camera);
      invalidate();
      return out;
    };

    if (captureViewsRef) captureViewsRef.current = capture;
    return () => {
      if (captureViewsRef) captureViewsRef.current = null;
    };
  }, [gl, scene, camera, controls, captureViewsRef]);

  return null;
}

function SceneContent({
  colors,
  overlayTextures,
  kitUvBounds,
  onKitUvBoundsReady,
  placementDragEnabled,
  activePlacement,
  onPlacementDragUv,
  captureViewsRef,
}) {
  return (
    <>
      <KitMultiViewCapture captureViewsRef={captureViewsRef} />
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 8, 5]} intensity={1.15} castShadow />
      <directionalLight position={[-4, 3, -4]} intensity={0.45} color="#b8c5ff" />
      <Stage environment="studio" intensity={0.55} adjustCamera={1.15} shadows="contact">
        <KitModel
          colors={colors}
          overlayTextures={overlayTextures}
          onKitUvBoundsReady={onKitUvBoundsReady}
          kitUvBounds={kitUvBounds}
        />
      </Stage>
      <Environment preset="studio" />
      <ContactShadows
        position={[0, -0.01, 0]}
        opacity={0.45}
        scale={12}
        blur={2.5}
        far={4}
      />
      <KitPlacementDragPlacer
        enabled={placementDragEnabled}
        activePlacement={activePlacement}
        kitUvBounds={kitUvBounds}
        onDragUv={onPlacementDragUv}
      />
      <OrbitControls
        makeDefault
        enableDamping
        dampingFactor={0.08}
        rotateSpeed={0.9}
        enablePan
        enableZoom
        enableRotate
        minDistance={0.8}
        maxDistance={10}
        maxPolarAngle={Math.PI * 0.92}
        minPolarAngle={0.1}
      />
    </>
  );
}

const FONT_OPTIONS = [
  { id: 'boldBlock', label: 'Bold Block' },
  { id: 'italicSports', label: 'Italic Sports' },
  { id: 'classicSerif', label: 'Classic Serif' },
  { id: 'modernSans', label: 'Modern Sans' },
];

const sketchPickerStyles = {
  default: {
    picker: {
      boxSizing: 'border-box',
      width: '100%',
      background: '#1e1e2e',
      border: '1px solid #2a2a3a',
      borderRadius: '8px',
      boxShadow: 'none',
    },
    saturation: { borderRadius: '6px' },
    controls: { flexDirection: 'row' },
    color: { width: '24px' },
    Hue: { borderRadius: '4px' },
  },
};

const kitIconSvgProps = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
  'aria-hidden': true,
};

function KitNavIcon({ name }) {
  const stroke = {
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  switch (name) {
    case 'colors':
      return (
        <svg {...kitIconSvgProps}>
          <circle cx="8" cy="8" r="2.75" fill="currentColor" stroke="none" />
          <circle cx="16" cy="8" r="2.75" fill="currentColor" stroke="none" />
          <circle cx="12" cy="15" r="2.75" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'presets':
      return (
        <svg {...kitIconSvgProps}>
          <path
            {...stroke}
            d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"
          />
        </svg>
      );
    case 'customLabels':
      return (
        <svg {...kitIconSvgProps}>
          <path {...stroke} d="M4 7h12M4 12h16M4 17h12" />
          <path {...stroke} d="M18 5v6l2 2-2 2" />
        </svg>
      );
    case 'customLogos':
      return (
        <svg {...kitIconSvgProps}>
          <rect x={3} y={5} width={14} height={14} rx={2} {...stroke} />
          <circle cx={8.5} cy={10} r={2} fill="currentColor" stroke="none" />
          <path {...stroke} d="M5 17h6l3-4 3 4" />
        </svg>
      );
    default:
      return null;
  }
}

/** Tab button only — use with `KitNavPanel` below a `kit-nav-row`. */
function KitNavTab({
  sectionKey,
  title,
  subtitle,
  iconName,
  isOpen,
  onToggle,
}) {
  const panelId = `kit-panel-${sectionKey}`;
  const btnId = `kit-section-${sectionKey}`;
  return (
    <div className={`kit-nav-card ${isOpen ? 'kit-nav-card--open' : ''}`}>
      <button
        type="button"
        className="kit-nav-card__trigger"
        id={btnId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="kit-nav-card__icon-wrap">
          <KitNavIcon name={iconName} />
        </span>
        <span className="kit-nav-card__label">
          <span className="kit-nav-card__title">{title}</span>
          <span className="kit-nav-card__subtitle">{subtitle}</span>
        </span>
        <span
          className={`kit-nav-card__chevron ${isOpen ? 'kit-nav-card__chevron--open' : ''}`}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>
    </div>
  );
}

function KitNavPanel({ sectionKey, isOpen, children }) {
  if (!isOpen) return null;
  const btnId = `kit-section-${sectionKey}`;
  const panelId = `kit-panel-${sectionKey}`;
  return (
    <div
      className="kit-nav-card__panel"
      id={panelId}
      role="region"
      aria-labelledby={btnId}
    >
      {children}
    </div>
  );
}

export default function KitDesigner3D({ onBack, onRequestOrder }) {
  const glRef = useRef(null);
  /** Set by {@link KitMultiViewCapture} — async () => viewImages map. */
  const captureViewsRef = useRef(null);
  const [orderCaptureBusy, setOrderCaptureBusy] = useState(false);
  const [colors, setColors] = useState(() => ({ ...DEFAULT_COLORS }));
  const [activePresetId, setActivePresetId] = useState(null);

  const [openSections, setOpenSections] = useState({
    colors: false,
    customLabels: false,
    customLogos: false,
    presets: false,
  });

  const [pickerOpen, setPickerOpen] = useState({
    body: false,
    collar: false,
    sleeveLeft: false,
    sleeveRight: false,
  });

  /** Optional lines on the back; each has text + UV position (0–1) within the back island. */
  const [customLabels, setCustomLabels] = useState(() => []);
  const [customLogos, setCustomLogos] = useState(() => []);
  const [placementDragEnabled, setPlacementDragEnabled] = useState(false);
  /** Which text box or logo is moved by drag on the 3D shirt. */
  const [activePlacement, setActivePlacement] = useState(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [kitOptions, setKitOptions] = useState({ fullSleeves: false, shirtColor: false });

  const [overlayTextures, setOverlayTextures] = useState(null);
  /** Increment after WebGL context loss to remount Canvas (unfreezes interaction). */
  const [glCanvasKey, setGlCanvasKey] = useState(0);
  const webglLossRemountCountRef = useRef(0);
  const [kitUvBounds, setKitUvBounds] = useState({
    front: null,
    back: null,
    wrap: null,
    side: null,
    torsoHorizontal: null,
    sleeveLeft: null,
    sleeveRight: null,
  });

  const handleKitUvBounds = useCallback((bounds) => {
    setKitUvBounds(bounds);
  }, []);

  const handlePlacementDragUv = useCallback((type, id, dragU, dragV, logoMeta) => {
    if (type === 'label') {
      const synced = syncSlidersFromTextureUv(dragU, dragV, kitUvBounds);
      setCustomLabels((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, dragU, dragV, u: synced.u, v: synced.v } : r
        )
      );
      return;
    }
    setCustomLogos((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const zone = logoMeta?.zone ?? r.zone;
        const synced = syncSlidersFromTextureUvForLogo(
          dragU,
          dragV,
          zone,
          kitUvBounds
        );
        return { ...r, zone, dragU, dragV, u: synced.u, v: synced.v };
      })
    );
  }, [kitUvBounds]);

  useEffect(() => {
    const labelIds = new Set(customLabels.map((r) => r.id));
    const logoIds = new Set(customLogos.map((r) => r.id));
    const valid =
      activePlacement &&
      (activePlacement.type === 'label'
        ? labelIds.has(activePlacement.id)
        : logoIds.has(activePlacement.id));
    if (!customLabels.length && !customLogos.length) {
      if (activePlacement !== null) setActivePlacement(null);
      return;
    }
    if (valid) return;
    if (customLabels.length) {
      setActivePlacement({
        type: 'label',
        id: customLabels[customLabels.length - 1].id,
      });
    } else {
      setActivePlacement({
        type: 'logo',
        id: customLogos[customLogos.length - 1].id,
      });
    }
  }, [customLabels, customLogos, activePlacement]);

  const toggleSection = useCallback((key) => {
    setOpenSections((prev) => {
      const isOpen = prev[key];
      // Close all, then open the clicked one (unless it was already open)
      return {
        colors: false,
        customLabels: false,
        customLogos: false,
        presets: false,
        [key]: !isOpen,
      };
    });
  }, []);

  const setZoneColor = useCallback((zone, hex) => {
    setColors((prev) => ({ ...prev, [zone]: hex }));
    setActivePresetId(null);
  }, []);

  const applyPreset = useCallback((preset) => {
    setColors({ ...preset.colors });
    setActivePresetId(preset.id);
  }, []);

  const kitTextureParams = useMemo(
    () => ({ customLabels, customLogos }),
    [customLabels, customLogos]
  );

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      (async () => {
        try {
          const tex = await buildAllKitOverlayTexturesAsync(
            kitTextureParams,
            kitUvBounds
          );
          if (cancelled || !tex) return;
          setOverlayTextures((prev) => {
            if (prev?.torso) prev.torso.dispose();
            if (prev?.sleeveLeft) prev.sleeveLeft.dispose();
            if (prev?.sleeveRight) prev.sleeveRight.dispose();
            return tex;
          });
        } catch (e) {
          console.warn('[KitDesigner3D] texture update', e);
        }
      })();
    }, KIT_OVERLAY_TEXTURE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [kitTextureParams, kitUvBounds]);

  const handleReset = useCallback(() => {
    setColors({ ...DEFAULT_COLORS });
    setCustomLabels([]);
    setCustomLogos([]);
    setKitOptions({ fullSleeves: false, shirtColor: false });
    setPlacementDragEnabled(false);
    setActivePlacement(null);
    setActivePresetId(null);
    setToast({ type: 'ok', text: 'Design reset' });
    setTimeout(() => setToast(null), 2000);
  }, []);

  const handleScreenshot = useCallback(() => {
    try {
      const gl = glRef.current;
      if (!gl || !gl.domElement) return;
      const dataUrl = gl.domElement.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `kit-design-${Date.now()}.png`;
      a.click();
    } catch (e) {
      console.error(e);
      setToast({ type: 'err', text: 'Screenshot failed' });
      setTimeout(() => setToast(null), 3000);
    }
  }, []);

  const handleAddToOrder = useCallback(async () => {
    if (typeof onRequestOrder !== 'function') {
      setToast({
        type: 'err',
        text: 'Order form is not available. Try again from the app menu.',
      });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    setOrderCaptureBusy(true);
    try {
      let viewImages = null;
      let compositeBase64 = '';
      if (captureViewsRef.current) {
        viewImages = await captureViewsRef.current();
      }
      if (viewImages) {
        compositeBase64 = await buildKitOrderCompositeImage(viewImages);
      }
      let screenshotBase64 = compositeBase64;
      if (!screenshotBase64) {
        const gl = glRef.current;
        if (gl?.domElement) {
          screenshotBase64 = gl.domElement.toDataURL('image/png');
        }
      }
      onRequestOrder({
        designColors: { ...colors },
        customLabels,
        customLogos,
        kitOptions,
        screenshotBase64,
        compositePreviewBase64: compositeBase64 || screenshotBase64,
        viewImages,
      });
    } catch (e) {
      console.warn('[KitDesigner3D] Order capture', e);
      setToast({
        type: 'err',
        text: 'Could not capture all views. Try again.',
      });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setOrderCaptureBusy(false);
    }
  }, [colors, customLabels, customLogos, kitOptions, onRequestOrder]);

  const sceneDragProps = useMemo(
    () => ({
      placementDragEnabled,
      activePlacement,
      onPlacementDragUv: handlePlacementDragUv,
    }),
    [placementDragEnabled, activePlacement, handlePlacementDragUv]
  );

  return (
    <div className="kit-page">
      <header className="kit-topbar">
        <div className="kit-topbar-left">
          <button
            type="button"
            className="kit-hamburger"
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
          />
          <span className="kit-logo">KIT DESIGNER</span>
        </div>
        <div className="kit-topbar-actions">
          <button type="button" className="kit-btn kit-btn-ghost" onClick={handleReset}>
            Reset Design
          </button>
          <button type="button" className="kit-btn kit-btn-ghost" onClick={handleScreenshot}>
            Take Screenshot
          </button>
          <button
            type="button"
            className="kit-btn kit-btn-primary"
            onClick={handleAddToOrder}
            disabled={orderCaptureBusy}
          >
            {orderCaptureBusy ? 'Capturing views…' : 'Add to Order'}
          </button>
        </div>
      </header>

      {sidebarOpen && (
        <button
          type="button"
          className="kit-sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className={`kit-layout ${sidebarOpen ? 'kit-layout--sidebar-open' : ''}`}>
        <aside className="kit-sidebar">
          <div className="kit-sidebar-inner">
            <div className="kit-sidebar-header-row">
              {onBack && (
                <button type="button" className="kit-back" onClick={onBack}>
                  ← Back
                </button>
              )}
              <button
                type="button"
                className="kit-sidebar-close"
                aria-label="Close menu"
                onClick={() => setSidebarOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="kit-sidebar-intro">
              <h2 className="kit-sidebar-intro__title">Customize kit</h2>
              <p className="kit-sidebar-intro__desc">
                Colors, custom back text, and presets update the 3D preview live.
              </p>
            </div>

            <div
              className="kit-nav-row"
              role="tablist"
              aria-label="Kit customization sections"
            >
              <KitNavTab
                sectionKey="colors"
                title="Colors"
                subtitle="Jersey, collar & sleeves"
                iconName="colors"
                isOpen={openSections.colors}
                onToggle={() => toggleSection('colors')}
              />
              <KitNavTab
                sectionKey="customLabels"
                title="Custom text"
                subtitle="Extra lines & placement"
                iconName="customLabels"
                isOpen={openSections.customLabels}
                onToggle={() => toggleSection('customLabels')}
              />
              <KitNavTab
                sectionKey="customLogos"
                title="Custom logos"
                subtitle="Upload PNG / JPG & place"
                iconName="customLogos"
                isOpen={openSections.customLogos}
                onToggle={() => toggleSection('customLogos')}
              />
              <KitNavTab
                sectionKey="presets"
                title="Kit styles"
                subtitle="One-tap color combinations"
                iconName="presets"
                isOpen={openSections.presets}
                onToggle={() => toggleSection('presets')}
              />
            </div>

            <div className="kit-nav-panels">
              <KitNavPanel sectionKey="colors" isOpen={openSections.colors}>
              {['body', 'collar', 'sleeveLeft', 'sleeveRight'].map((zone) => (
                <div key={zone} className="kit-color-row">
                  <label className="kit-zone-label">
                    {zone === 'sleeveLeft'
                      ? 'Left Sleeve'
                      : zone === 'sleeveRight'
                        ? 'Right Sleeve'
                        : zone.charAt(0).toUpperCase() + zone.slice(1)}
                  </label>
                  <button
                    type="button"
                    className="kit-swatch"
                    style={{ backgroundColor: colors[zone] }}
                    onClick={() =>
                      setPickerOpen((p) => ({
                        ...p,
                        [zone]: !p[zone],
                      }))
                    }
                  />
                  {pickerOpen[zone] && (
                    <div className="kit-picker-pop">
                      <SketchPicker
                        color={colors[zone]}
                        onChange={(c) => setZoneColor(zone, c.hex)}
                        width="100%"
                        styles={sketchPickerStyles}
                      />
                    </div>
                  )}
                </div>
              ))}

              {/* Kit options checkboxes */}
              <div className="kit-options-section">
                <label className="kit-checkbox-row">
                  <input
                    type="checkbox"
                    checked={kitOptions.fullSleeves}
                    onChange={(e) => setKitOptions((o) => ({ ...o, fullSleeves: e.target.checked }))}
                  />
                  <span className="kit-zone-label" style={{ marginBottom: 0 }}>Full Sleeves</span>
                </label>
                <label className="kit-checkbox-row">
                  <input
                    type="checkbox"
                    checked={kitOptions.shirtColor}
                    onChange={(e) => setKitOptions((o) => ({ ...o, shirtColor: e.target.checked }))}
                  />
                  <span className="kit-zone-label" style={{ marginBottom: 0 }}>Custom Shirt Color</span>
                </label>
              </div>
              </KitNavPanel>

              <KitNavPanel sectionKey="customLabels" isOpen={openSections.customLabels}>
                <p className="kit-field-hint">
                  Horizontal 0–100% follows the torso around the body (object-space angle), then maps to
                  real UVs — not a straight line in the atlas. Very long lines can still clip at a seam —
                  reduce size. Vertical = waist toward shoulders.
                </p>
                <label className="kit-mini-label kit-checkbox-row" style={{ marginBottom: '0.65rem' }}>
                  <input
                    type="checkbox"
                    checked={placementDragEnabled}
                    onChange={(e) => setPlacementDragEnabled(e.target.checked)}
                  />
                  Click / drag on the shirt to place; drag on empty space to rotate the view
                </label>
                <button
                  type="button"
                  className="kit-btn kit-btn-primary full"
                  style={{ marginBottom: '0.75rem' }}
                  onClick={() => {
                    const row = createCustomLabelRow();
                    setCustomLabels((prev) => [...prev, row]);
                    setActivePlacement({ type: 'label', id: row.id });
                  }}
                >
                  + Add text box
                </button>
                {customLabels.length === 0 && (
                  <p className="kit-field-hint" style={{ marginTop: 0 }}>
                    No custom lines yet — use the button above to add one.
                  </p>
                )}
                {customLabels.map((row, idx) => (
                  <div
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActivePlacement({ type: 'label', id: row.id })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        setActivePlacement({ type: 'label', id: row.id });
                    }}
                    style={{
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid #2a2a3a',
                      borderLeft:
                        activePlacement?.type === 'label' && activePlacement.id === row.id
                          ? '3px solid #c94b7d'
                          : '3px solid transparent',
                      paddingLeft: '0.5rem',
                      marginLeft: '-0.5rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.35rem',
                      }}
                    >
                      <span className="kit-mini-label" style={{ marginBottom: 0 }}>
                        Text box {idx + 1}
                        {activePlacement?.type === 'label' &&
                          activePlacement.id === row.id &&
                          placementDragEnabled && (
                          <span style={{ color: '#c94b7d', fontWeight: 600 }}> — active for drag</span>
                        )}
                      </span>
                      <button
                        type="button"
                        className="kit-btn kit-btn-ghost"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCustomLabels((prev) => prev.filter((r) => r.id !== row.id));
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    <textarea
                      className="kit-input"
                      rows={2}
                      value={row.text}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        setCustomLabels((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, text: e.target.value } : r
                          )
                        )
                      }
                      placeholder="Write your text here…"
                    />
                    <label className="kit-mini-label">Font</label>
                    <select
                      className="kit-select"
                      value={row.fontId}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        setCustomLabels((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, fontId: e.target.value } : r
                          )
                        )
                      }
                    >
                      {FONT_OPTIONS.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                    <label className="kit-mini-label">Size ({row.size}px)</label>
                    <input
                      type="range"
                      className="kit-range"
                      min={10}
                      max={72}
                      value={row.size}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        setCustomLabels((prev) =>
                          prev.map((r) =>
                            r.id === row.id
                              ? { ...r, size: Number(e.target.value) }
                              : r
                          )
                        )
                      }
                    />
                    <label className="kit-mini-label">
                      Horizontal ({Math.round(row.u * 100)}% — 0 left, 100 right)
                    </label>
                    <input
                      type="range"
                      className="kit-range"
                      min={0}
                      max={100}
                      value={Math.round(row.u * 100)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const u = Number(e.target.value) / 100;
                        setCustomLabels((prev) =>
                          prev.map((r) =>
                            r.id === row.id
                              ? { ...r, u, dragU: undefined, dragV: undefined }
                              : r
                          )
                        );
                      }}
                    />
                    <label className="kit-mini-label">
                      Vertical ({Math.round(row.v * 100)}% — 0 waist hem, 100 shoulders)
                    </label>
                    <input
                      type="range"
                      className="kit-range"
                      min={0}
                      max={100}
                      value={Math.round(row.v * 100)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const v = Number(e.target.value) / 100;
                        setCustomLabels((prev) =>
                          prev.map((r) =>
                            r.id === row.id
                              ? { ...r, v, dragU: undefined, dragV: undefined }
                              : r
                          )
                        );
                      }}
                    />
                    <label className="kit-mini-label">Color</label>
                    <input
                      type="color"
                      value={row.color}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        setCustomLabels((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, color: e.target.value } : r
                          )
                        )
                      }
                      style={{
                        width: '100%',
                        height: 36,
                        border: '1px solid #2a2a3a',
                        borderRadius: 6,
                        cursor: 'pointer',
                        background: '#12121a',
                      }}
                    />
                  </div>
                ))}
              </KitNavPanel>

              <KitNavPanel sectionKey="customLogos" isOpen={openSections.customLogos}>
                <p className="kit-field-hint">
                  Up to four logos. Turn on placement mode below, select a logo slot, then click the
                  chest, back, or a sleeve on the 3D shirt — same as text. The placement area updates from
                  your click; you can still adjust with the sliders.
                </p>
                <label className="kit-mini-label kit-checkbox-row" style={{ marginBottom: '0.65rem' }}>
                  <input
                    type="checkbox"
                    checked={placementDragEnabled}
                    onChange={(e) => setPlacementDragEnabled(e.target.checked)}
                  />
                  Click / drag on the shirt to place; drag on empty space to rotate the view
                </label>
                <button
                  type="button"
                  className="kit-btn kit-btn-primary full"
                  style={{ marginBottom: '0.75rem' }}
                  disabled={customLogos.length >= MAX_CUSTOM_LOGOS}
                  onClick={() => {
                    if (customLogos.length >= MAX_CUSTOM_LOGOS) return;
                    const row = createCustomLogoRow();
                    setCustomLogos((prev) => [...prev, row]);
                    setActivePlacement({ type: 'logo', id: row.id });
                  }}
                >
                  + Add logo slot
                  {customLogos.length >= MAX_CUSTOM_LOGOS ? ' (max 4)' : ''}
                </button>
                {customLogos.length === 0 && (
                  <p className="kit-field-hint" style={{ marginTop: 0 }}>
                    No logos yet — add a slot, then choose an image file.
                  </p>
                )}
                {customLogos.map((row, idx) => (
                  <div
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActivePlacement({ type: 'logo', id: row.id })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        setActivePlacement({ type: 'logo', id: row.id });
                    }}
                    style={{
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid #2a2a3a',
                      borderLeft:
                        activePlacement?.type === 'logo' && activePlacement.id === row.id
                          ? '3px solid #c94b7d'
                          : '3px solid transparent',
                      paddingLeft: '0.5rem',
                      marginLeft: '-0.5rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.35rem',
                      }}
                    >
                      <span className="kit-mini-label" style={{ marginBottom: 0 }}>
                        Logo {idx + 1}
                        {activePlacement?.type === 'logo' &&
                          activePlacement.id === row.id &&
                          placementDragEnabled && (
                            <span style={{ color: '#c94b7d', fontWeight: 600 }}>
                              {' '}
                              — active for drag
                            </span>
                          )}
                      </span>
                      <button
                        type="button"
                        className="kit-btn kit-btn-ghost"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCustomLogos((prev) => prev.filter((r) => r.id !== row.id));
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    <label className="kit-mini-label">Placement area (auto from shirt click)</label>
                    <select
                      className="kit-input"
                      value={row.zone || 'chest'}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const zone = e.target.value;
                        setCustomLogos((prev) =>
                          prev.map((r) =>
                            r.id === row.id
                              ? { ...r, zone, dragU: undefined, dragV: undefined }
                              : r
                          )
                        );
                      }}
                      style={{ marginBottom: '0.5rem', width: '100%' }}
                    >
                      {LOGO_ZONE_OPTIONS.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.label}
                        </option>
                      ))}
                    </select>
                    <label className="kit-mini-label">Image file</label>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="kit-input"
                      style={{ padding: '0.35rem', fontSize: '0.85rem' }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        e.target.value = '';
                        if (!file || !file.type.startsWith('image/')) return;
                        if (file.size > 2.5 * 1024 * 1024) {
                          setToast({
                            type: 'err',
                            text: 'Image must be under 2.5 MB',
                          });
                          setTimeout(() => setToast(null), 3000);
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = () => {
                          const dataUrl = reader.result;
                          setCustomLogos((prev) =>
                            prev.map((r) =>
                              r.id === row.id
                                ? { ...r, imageDataUrl: dataUrl, dragU: undefined, dragV: undefined }
                                : r
                            )
                          );
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                    {row.imageDataUrl ? (
                      <div
                        style={{
                          marginTop: '0.5rem',
                          borderRadius: 8,
                          overflow: 'hidden',
                          border: '1px solid #2a2a3a',
                          maxHeight: 100,
                          background: '#12121a',
                        }}
                      >
                        <img
                          src={row.imageDataUrl}
                          alt=""
                          style={{ width: '100%', maxHeight: 100, objectFit: 'contain' }}
                        />
                      </div>
                    ) : (
                      <p className="kit-field-hint" style={{ marginTop: '0.35rem' }}>
                        Choose a file to preview on the shirt.
                      </p>
                    )}
                    <label className="kit-mini-label">
                      Size ({Math.round((row.scale || 0.22) * 100)}% of texture width)
                    </label>
                    <input
                      type="range"
                      className="kit-range"
                      min={6}
                      max={48}
                      value={Math.round((row.scale || 0.22) * 100)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const scale = Number(e.target.value) / 100;
                        setCustomLogos((prev) =>
                          prev.map((r) =>
                            r.id === row.id
                              ? { ...r, scale, dragU: undefined, dragV: undefined }
                              : r
                          )
                        );
                      }}
                    />
                    <label className="kit-mini-label">
                      Horizontal ({Math.round(row.u * 100)}% — 0 left, 100 right)
                    </label>
                    <input
                      type="range"
                      className="kit-range"
                      min={0}
                      max={100}
                      value={Math.round(row.u * 100)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const u = Number(e.target.value) / 100;
                        setCustomLogos((prev) =>
                          prev.map((r) =>
                            r.id === row.id
                              ? { ...r, u, dragU: undefined, dragV: undefined }
                              : r
                          )
                        );
                      }}
                    />
                    <label className="kit-mini-label">
                      Vertical ({Math.round(row.v * 100)}% — 0 waist, 100 shoulders)
                    </label>
                    <input
                      type="range"
                      className="kit-range"
                      min={0}
                      max={100}
                      value={Math.round(row.v * 100)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const v = Number(e.target.value) / 100;
                        setCustomLogos((prev) =>
                          prev.map((r) =>
                            r.id === row.id
                              ? { ...r, v, dragU: undefined, dragV: undefined }
                              : r
                          )
                        );
                      }}
                    />
                  </div>
                ))}
              </KitNavPanel>

              <KitNavPanel sectionKey="presets" isOpen={openSections.presets}>
              <div className="kit-preset-grid">
                {STYLE_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`kit-preset-card ${activePresetId === p.id ? 'active' : ''}`}
                    onClick={() => applyPreset(p)}
                  >
                    <div className="kit-preset-preview">
                      {p.preview.map((c, i) => (
                        <span key={i} style={{ background: c }} />
                      ))}
                    </div>
                    <span className="kit-preset-name">{p.name}</span>
                  </button>
                ))}
              </div>
              </KitNavPanel>
            </div>

            <div className="kit-preview-strip">
              <span>Body</span>
              <div style={{ background: colors.body }} />
              <span>Collar</span>
              <div style={{ background: colors.collar }} />
              <span>L</span>
              <div style={{ background: colors.sleeveLeft }} />
              <span>R</span>
              <div style={{ background: colors.sleeveRight }} />
            </div>
          </div>
        </aside>

        <div className="kit-canvas-wrap">
          <div className="kit-canvas-inner">
          <Canvas
            key={glCanvasKey}
            shadows
            dpr={[1, KIT_MAX_DPR]}
            camera={{ position: [0, 0.4, 2.6], fov: 45 }}
            gl={{
              preserveDrawingBuffer: true,
              antialias: true,
              powerPreference: 'default',
              alpha: false,
              stencil: false,
              depth: true,
            }}
            onCreated={({ gl }) => {
              glRef.current = gl;
              gl.setClearColor('#0a0a0f');
              const el = gl.domElement;
              const onCtxLost = (e) => {
                e.preventDefault();
                console.warn('[KitDesigner3D] WebGL context lost — remounting canvas');
                webglLossRemountCountRef.current += 1;
                if (webglLossRemountCountRef.current > 5) {
                  setToast({
                    type: 'err',
                    text: 'GPU reset too many times — refresh the page or close other tabs.',
                  });
                  setTimeout(() => setToast(null), 6000);
                  return;
                }
                setToast({
                  type: 'err',
                  text: 'Graphics reset — restoring 3D view…',
                });
                setTimeout(() => setToast(null), 4000);
                queueMicrotask(() => setGlCanvasKey((k) => k + 1));
              };
              el.addEventListener('webglcontextlost', onCtxLost);
            }}
          >
            <Suspense fallback={<Loader />}>
              <SceneContent
                colors={colors}
                overlayTextures={overlayTextures}
                kitUvBounds={kitUvBounds}
                onKitUvBoundsReady={handleKitUvBounds}
                captureViewsRef={captureViewsRef}
                {...sceneDragProps}
              />
            </Suspense>
          </Canvas>
          </div>
        </div>
      </div>

      {toast && (
        <div className={`kit-toast ${toast.type === 'ok' ? 'kit-toast--ok' : 'kit-toast--err'}`}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

useGLTF.preload(MODEL_URL);
